import { Component, OnInit } from "@angular/core";
import { environment } from "../../environments/environment";
import { RouterModule, Router } from "@angular/router";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatCardModule } from "@angular/material/card";
import { MatMenuModule } from "@angular/material/menu";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";

import { KeycloakService } from "keycloak-angular";
import { KeycloakProfile } from "keycloak-js";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { PortalService, Portal, PortalAccess } from "@kelyn-technologies/dsio-angular-lib/dsio-api";
import {
  AuthorizationRequestService,
  ZendeskWebwidgetService,
} from "@kelyn-technologies/dsio-angular-lib";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { finalize, switchMap } from "rxjs/operators";

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  imports: [
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatGridListModule,
    MatCardModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatSnackBarModule,
  ],
  styleUrls: ["./home.component.css"],
})
export class HomeComponent implements OnInit {
  public userProfile: KeycloakProfile | null = null;
  public firstName: string | undefined = "";

  portals: Portal[] = [];
  environment = environment;

  isRequestingAccess = false;
  portalAccessInFlight: Record<string, boolean> = {};
  portalAccessStatus: string | null = null;
  portalAccessStatusPortalId: string | null = null;
  private portalStatusTimeout: ReturnType<typeof setTimeout> | null = null;

  private readonly portalAccessLoader = (portalId: string) =>
    () => this.portalService.getPortalAccess(portalId);

  public tenantId: string = sessionStorage.getItem("tenantId") as string;
  public get atlasSubscriptionUrl(): string {
    const baseUrl = this.environment.REGISTRATION_URL;
    if (!baseUrl) {
      return "#";
    }
    const tenantQuery = this.tenantId
      ? `?tenantId=${encodeURIComponent(this.tenantId)}`
      : "";
    return `${baseUrl}/atlas/subscribe${tenantQuery}`;
  }
  constructor(
    private readonly keycloak: KeycloakService,
    private router: Router,
    private portalService: PortalService,
    private readonly authorizationRequestService: AuthorizationRequestService,
    private zendeskWidget: ZendeskWebwidgetService,
    private readonly snackBar: MatSnackBar
  ) {
    this.zendeskWidget.initZendesk().then(() => {
      console.log("Zendesk widget initialized");
      this.zendeskWidget.loginUser((error) => {
        if (error) {
          console.error("Failed to login to Zendesk:", error);
          this.zendeskWidget.zE("messenger", "hide");
        } else {
          console.log("Successfully logged in to Zendesk");
        }
      });
    });
  }

  ngOnInit(): void {
    this.getPortals();
  }

  private getPortals(): void {
    if (!this.tenantId) return;

    this.portalService.getPortals().subscribe({
      next: (response) => {
        if (response.portals != null) {
          this.portals = response.portals;
        } else {
          this.portals = [];
        }
      },
      error: (err) => {
        console.error("Failed to load portals:", err);
        this.portals = [];
      },
    });
  }

  openPortal(url: string): void {
    let fullUrl = url;
    if (url.startsWith("http://")) {
      fullUrl = fullUrl.replace("http://", "https://");
    } else if (!url.startsWith("https://")) {
      fullUrl = `https://${url}`;
    }
    window.open(fullUrl, "_blank");
  }

  handlePortalClick(portal: Portal): void {
    const portalId = portal.portal_id;
    if (!portalId) {
      this.showMessage("Portal ID is missing.");
      return;
    }

    if (this.portalAccessInFlight[portalId]) {
      this.showMessage("Portal access is already in progress.");
      return;
    }

    this.portalAccessInFlight[portalId] = true;
    this.setPortalStatus("Checking portal access...", portalId);

    this.isRequestingAccess = true;
    this.resolvePortalAccess(portalId).subscribe({
      next: (result) => {
        this.isRequestingAccess = false;
        this.handlePortalAuthorizationResult(result);
      },
      error: (err: HttpErrorResponse) => {
        this.isRequestingAccess = false;
        if (err?.status === 401) {
          this.showMessage("Missing authorized tenant.");
        } else if (err?.status === 403) {
          this.showMessage("Access denied for this portal.");
        } else {
          this.showMessage("Failed to get portal access. Please try again.");
        }
        this.setPortalStatus("Portal access failed.", portalId);
      },
      complete: () => {
        this.portalAccessInFlight[portalId] = false;
      },
    });
  }

  private handlePortalAuthorizationResult(
    result: PortalAccessOutcome
  ): void {
    switch (result.kind) {
      case "navigate":
        this.setPortalStatus("Opening portal...", this.portalAccessStatusPortalId ?? undefined, 3000);
        this.openPortal(result.url);
        return;
      case "needs_authorization":
        this.setPortalStatus("Authorization required. Redirecting...", result.portalId);
        this.router.navigate(["/gateway/authorizations"], {
          queryParams: { portalId: result.portalId, requestId: result.requestId },
        });
        return;
      case "deny":
        this.showMessage("No applicable grants for this portal.");
        this.setPortalStatus("No applicable grants for this portal.", this.portalAccessStatusPortalId ?? undefined);
        return;
    }
  }

  private resolvePortalAccess(
    portalId: string
  ): Observable<PortalAccessOutcome> {
    const fetchAccess = this.portalAccessLoader(portalId);
    return fetchAccess().pipe(
      switchMap((access) => this.handlePortalAccess(portalId, access, fetchAccess)),
      finalize(() => {
        this.portalAccessInFlight[portalId] = false;
      })
    );
  }

  private handlePortalAccess(
    portalId: string,
    access: PortalAccess,
    fetchAccess: () => Observable<PortalAccess>
  ): Observable<PortalAccessOutcome> {
    switch (access.action) {
      case "navigate":
        return this.toNavigateResult(access);
      case "request_access": {
        this.setPortalStatus("Requesting portal access...", portalId);
        const grantId = access.target?.trim();
        if (!grantId) {
          return throwError(() => new Error("Grant ID is missing for this portal."));
        }
        return this.authorizationRequestService
          .createAuthorizationRequest(grantId)
          .pipe(
            switchMap((authorizationRequest) => {
              if (authorizationRequest.is_valid) {
                return fetchAccess().pipe(
                  switchMap((nextAccess) => {
                    if (nextAccess.action !== "navigate") {
                      return throwError(
                        () =>
                          new Error("Portal access still requires authorization.")
                      );
                    }
                    return this.toNavigateResult(nextAccess);
                  })
                );
              }

              const requestId = authorizationRequest.id?.trim();
              if (!requestId) {
                return throwError(
                  () => new Error("Authorization request ID is missing.")
                );
              }
              const outcome: PortalAccessOutcome = {
                kind: "needs_authorization",
                portalId,
                requestId,
              };
              return of(outcome);
            })
          );
      }
      case "update_access": {
        this.setPortalStatus("Authorization required. Review request.", portalId);
        const requestId = access.target?.trim();
        if (!requestId) {
          return throwError(
            () => new Error("Authorization request ID is missing.")
          );
        }
        const outcome: PortalAccessOutcome = {
          kind: "needs_authorization",
          portalId,
          requestId,
        };
        return of(outcome);
      }
      case "deny":
        this.setPortalStatus("No applicable grants for this portal.", portalId);
        return of({ kind: "deny" });
      default:
        return throwError(
          () => new Error("Unknown portal access action received.")
        );
    }
  }

  private toNavigateResult(access: PortalAccess): Observable<PortalAccessOutcome> {
    const url = this.resolvePortalUrl(access);
    if (!url) {
      return throwError(
        () => new Error("Navigate action returned without a URL.")
      );
    }
    return of({ kind: "navigate", url });
  }

  private resolvePortalUrl(access: PortalAccess): string | null {
    const targetUrl = access.target?.trim();
    const dataUrl = (access.data as { url?: string } | undefined)?.url?.trim();
    return targetUrl || dataUrl || null;
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, "Close", { duration: 5000 });
  }

  isPortalBusy(portal: Portal): boolean {
    const portalId = portal.portal_id;
    return !!portalId && !!this.portalAccessInFlight[portalId];
  }

  private setPortalStatus(message: string, portalId?: string, clearAfterMs?: number): void {
    this.portalAccessStatus = message;
    this.portalAccessStatusPortalId = portalId ?? null;
    this.showMessage(message);
    if (this.portalStatusTimeout) {
      clearTimeout(this.portalStatusTimeout);
      this.portalStatusTimeout = null;
    }
    if (clearAfterMs && clearAfterMs > 0) {
      this.portalStatusTimeout = setTimeout(() => {
        this.portalAccessStatus = null;
        this.portalAccessStatusPortalId = null;
        this.portalStatusTimeout = null;
      }, clearAfterMs);
    }
  }

  formatType(type: string): string {
    switch (type) {
      case "aws":
        return "AWS Account";
      case "commvault":
        return "Commvault Account";
      default:
        return type;
    }
  }

  public logout(): void {
    this.keycloak.logout(`${environment.FRONTEND_URL}/gateway/auth`);
  }
}

type PortalAccessOutcome =
  | { kind: "navigate"; url: string }
  | { kind: "needs_authorization"; requestId: string; portalId: string }
  | { kind: "deny" };
